import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  ChevronLeft,
  ChevronRight,
  Columns3,
  Download,
  Filter,
  Layers3,
  Pin,
  Plus,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { taxText } from "../terminology";
import { useFormValidation } from "../form-validation";
import { ValidationSummary, FieldError } from "./ValidationSummary";
import "./domain.css";

export type TableRecord = Record<string, unknown>;
export type FilterOperator =
  | "contains"
  | "not_contains"
  | "is"
  | "is_not"
  | "starts_with"
  | "greater_than"
  | "less_than"
  | "is_empty"
  | "is_not_empty";
export interface TableFilter {
  id: string;
  field: string;
  operator: FilterOperator;
  value: string;
}
export interface TableView {
  id: string;
  name: string;
  visibleColumns: string[];
  filters: TableFilter[];
  sort: { key: string; direction: "asc" | "desc" } | null;
  groupBy: string;
  pageSize: number;
  query?: string;
}
export interface TableColumn<T extends TableRecord = TableRecord> {
  key: string;
  label: string;
  render?: (value: unknown, row: T) => ReactNode;
  getTitle?: (value: unknown, row: T) => string | undefined;
  sortable?: boolean;
}
export interface DataTableProps<T extends TableRecord = TableRecord> {
  records: T[];
  columns: TableColumn<T>[];
  title?: string;
  getRowId?: (row: T, index: number) => string;
  getSearchText?: (row: T) => string;
  noResultsTitle?: string;
  onInspect?: (row: T) => void;
  onSaveView?: (view: TableView) => void | Promise<void>;
  savedViews?: TableView[];
  allowExport?: boolean;
  bulkActions?: {
    label: string;
    onAction: (rows: T[]) => void | Promise<void>;
  }[];
  pinnedIds?: string[];
  onPinChange?: (ids: string[]) => void | Promise<void>;
  storageKey?: string;
  allowPersistNonPrivateIds?: boolean;
  loading?: boolean;
  emptyMessage?: string;
}

export function columnDisplayText<T extends TableRecord>(
  record: T,
  column?: TableColumn<T>,
): string {
  const value = valueAt(record, column?.key ?? "");
  return column?.getTitle?.(value, record) ?? tableText(value);
}

export function valueAt(record: TableRecord, key: string): unknown {
  if (Object.prototype.hasOwnProperty.call(record, key)) return record[key];
  return key
    .split(".")
    .reduce<unknown>(
      (value, part) =>
        value &&
        typeof value === "object" &&
        Object.prototype.hasOwnProperty.call(value, part)
          ? (value as TableRecord)[part]
          : undefined,
      record,
    );
}
export function tableText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "[Unserializable value]";
    }
  }
  return String(value);
}
export function compareValues(a: unknown, b: unknown): number {
  const left = tableText(a),
    right = tableText(b);
  const decimal = /^(-?)(\d+)(?:\.(\d+))?$/;
  const x = left.match(decimal),
    y = right.match(decimal);
  if (x && y && left.length < 500 && right.length < 500) {
    const scale = Math.max(x[3]?.length ?? 0, y[3]?.length ?? 0);
    const l = BigInt(`${x[1]}${x[2]}${(x[3] ?? "").padEnd(scale, "0")}`);
    const r = BigInt(`${y[1]}${y[2]}${(y[3] ?? "").padEnd(scale, "0")}`);
    return l < r ? -1 : l > r ? 1 : 0;
  }
  return left.localeCompare(right, "en", {
    numeric: true,
    sensitivity: "base",
  });
}
export function matchesFilter(
  record: TableRecord,
  filter: TableFilter,
): boolean {
  const value = valueAt(record, filter.field),
    text = tableText(value).toLocaleLowerCase("en"),
    target = filter.value.toLocaleLowerCase("en");
  const empty =
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && !value.length);
  switch (filter.operator) {
    case "contains":
      return text.includes(target);
    case "not_contains":
      return !text.includes(target);
    case "is":
      return text === target;
    case "is_not":
      return text !== target;
    case "starts_with":
      return text.startsWith(target);
    case "greater_than":
      return !empty && compareValues(value, filter.value) > 0;
    case "less_than":
      return !empty && compareValues(value, filter.value) < 0;
    case "is_empty":
      return empty;
    case "is_not_empty":
      return !empty;
  }
}
/** Spreadsheet formulas are neutralized, including formulas hidden behind whitespace. */
export function escapeCsvCell(value: unknown): string {
  let text = tableText(value);
  if (/^[\s]*[=+\-@]/u.test(text) || /^[\t\r\n]/.test(text)) text = `'${text}`;
  return `"${text.replaceAll('"', '""')}"`;
}
export function recordsToCsv<T extends TableRecord>(
  records: T[],
  columns: TableColumn<T>[],
): string {
  return [
    columns.map((column) => escapeCsvCell(column.label)).join(","),
    ...records.map((record) =>
      columns
        .map((column) => escapeCsvCell(valueAt(record, column.key)))
        .join(","),
    ),
  ].join("\r\n");
}
/** Remotely saved views arrive through savedViews; never create a second local identity. */
export function sessionViewsAfterSave(
  current: TableView[],
  view: TableView,
  savedRemotely: boolean,
): TableView[] {
  return savedRemotely ? current : [...current, view];
}
/** Presentation aliases supplement raw-column search without modifying source values. */
export function matchesTableSearch<T extends TableRecord>(
  record: T,
  columns: TableColumn<T>[],
  query: string,
  getSearchText?: (record: T) => string,
): boolean {
  const needle = query.trim().toLocaleLowerCase("en");
  return (
    !needle ||
    columns.some((column) =>
      tableText(valueAt(record, column.key))
        .toLocaleLowerCase("en")
        .includes(needle),
    ) ||
    tableText(getSearchText?.(record)).toLocaleLowerCase("en").includes(needle)
  );
}

const operators: { value: FilterOperator; label: string }[] = [
  { value: "contains", label: "contains" },
  { value: "not_contains", label: "does not contain" },
  { value: "is", label: "is exactly" },
  { value: "is_not", label: "is not" },
  { value: "starts_with", label: "starts with" },
  { value: "greater_than", label: "is greater than" },
  { value: "less_than", label: "is less than" },
  { value: "is_empty", label: "is empty" },
  { value: "is_not_empty", label: "is not empty" },
];
function readPins(key?: string, permitted = false): Set<string> {
  if (!key || !permitted || typeof window === "undefined") return new Set();
  try {
    const value: unknown = JSON.parse(
      localStorage.getItem(`zentrotax:pins:${key}`) ?? "[]",
    );
    return new Set(
      Array.isArray(value)
        ? value
            .filter(
              (id): id is string => typeof id === "string" && id.length <= 300,
            )
            .slice(0, 10000)
        : [],
    );
  } catch {
    return new Set();
  }
}
function SelectionBox({
  checked,
  mixed,
  label,
  onChange,
}: {
  checked: boolean;
  mixed?: boolean;
  label: string;
  onChange: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (input.current) input.current.indeterminate = Boolean(mixed);
  }, [mixed]);
  return (
    <input
      ref={input}
      className="zt-check"
      type="checkbox"
      aria-label={label}
      checked={checked}
      onChange={onChange}
    />
  );
}

export function DataTable<T extends TableRecord>({
  records,
  columns,
  title = "Records",
  getRowId,
  getSearchText,
  noResultsTitle,
  onInspect,
  onSaveView,
  savedViews = [],
  bulkActions = [],
  pinnedIds,
  onPinChange,
  storageKey,
  allowPersistNonPrivateIds = false,
  allowExport = true,
  loading = false,
  emptyMessage = "There are no records in this workspace yet.",
}: DataTableProps<T>) {
  const instance = useId();
  const displayTitle = taxText(title);
  const returnTable = /^(returns|return assignments)$/i.test(displayTitle);
  const filteredEmptyTitle =
    noResultsTitle ??
    (returnTable ? "No returns found" : "No matching records");
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState<string[]>(() =>
    columns.map((column) => column.key),
  );
  const [filters, setFilters] = useState<TableFilter[]>([]);
  const [sort, setSort] = useState<TableView["sort"]>(null);
  const [groupBy, setGroupBy] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pins, setPins] = useState(() =>
    pinnedIds === undefined
      ? readPins(storageKey, allowPersistNonPrivateIds)
      : new Set(pinnedIds),
  );
  const [pinBusy, setPinBusy] = useState(false);
  const pinMutationPending = useRef(false);
  const latestPinnedIds = useRef(pinnedIds);
  latestPinnedIds.current = pinnedIds;
  const pinSignature =
    pinnedIds === undefined
      ? null
      : JSON.stringify([...new Set(pinnedIds)].sort());
  const [panel, setPanel] = useState<"columns" | "filters" | "views" | null>(
    null,
  );
  const [sessionViews, setSessionViews] = useState<TableView[]>([]);
  const [viewName, setViewName] = useState("");
  const viewValidation = useFormValidation(
    `${displayTitle} — Saved views`,
    panel === "views"
      ? [
          {
            id: `${instance}-view-name`,
            label: "View name",
            value: viewName,
            required: true,
            maxLength: 80,
          },
        ]
      : [],
  );
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const columnSignature = columns.map((column) => column.key).join("\u0000");
  const previousColumns = useRef(columnSignature);
  const filterSerial = useRef(0);
  const panelToggle = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    // Hydration is read-only. It never invokes a replacement callback or deletes server pins.
    if (pinSignature !== null)
      setPins(new Set(JSON.parse(pinSignature) as string[]));
    else setPins(readPins(storageKey, allowPersistNonPrivateIds));
  }, [pinSignature, storageKey, allowPersistNonPrivateIds]);
  useEffect(() => {
    if (previousColumns.current !== columnSignature) {
      const previous = new Set(previousColumns.current.split("\u0000"));
      const keys = columnSignature.split("\u0000").filter(Boolean);
      setVisible((current) =>
        keys.filter((key) => current.includes(key) || !previous.has(key)),
      );
      setFilters((current) =>
        current.filter((filter) => keys.includes(filter.field)),
      );
      setSort((current) =>
        current && keys.includes(current.key) ? current : null,
      );
      setGroupBy((current) => (keys.includes(current) ? current : ""));
      previousColumns.current = columnSignature;
    }
  }, [columnSignature]);

  const identified = useMemo(() => {
    const rows = records.map((record, index) => ({
      record,
      id: getRowId
        ? getRowId(record, index)
        : tableText(
            record.id ??
              record.contract_id ??
              record.rule_id ??
              record.object_id ??
              record.test_id ??
              record.artifact_id ??
              record.path,
          ) || `row:${index}`,
      index,
    }));
    const occurrences = new Map<string, number>();
    rows.forEach((row) =>
      occurrences.set(row.id, (occurrences.get(row.id) ?? 0) + 1),
    );
    return rows.map((row) => ({
      ...row,
      id:
        (occurrences.get(row.id) ?? 0) > 1
          ? `${row.id}:occurrence:${row.index}`
          : row.id,
    }));
  }, [records, getRowId]);
  const filtered = useMemo(
    () =>
      identified
        .filter(
          ({ record }) =>
            matchesTableSearch(record, columns, query, getSearchText) &&
            filters.every((filter) => matchesFilter(record, filter)),
        )
        .sort((a, b) => {
          const pinOrder = Number(pins.has(b.id)) - Number(pins.has(a.id));
          if (pinOrder) return pinOrder;
          if (sort) {
            const compared = compareValues(
              valueAt(a.record, sort.key),
              valueAt(b.record, sort.key),
            );
            if (compared)
              return sort.direction === "asc" ? compared : -compared;
          }
          return a.index - b.index;
        }),
    [identified, query, columns, filters, sort, pins, getSearchText],
  );
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const effectivePage = Math.min(page, pages);
  const pageRows = filtered.slice(
    (effectivePage - 1) * pageSize,
    effectivePage * pageSize,
  );
  const activeColumns = columns.filter((column) =>
    visible.includes(column.key),
  );
  const selectedRows = identified.filter((row) => selected.has(row.id));
  const allPageSelected =
    pageRows.length > 0 && pageRows.every((row) => selected.has(row.id));
  const somePageSelected = pageRows.some((row) => selected.has(row.id));
  const allViews = useMemo(
    () => [
      ...new Map(
        [...savedViews, ...sessionViews].map((view) => [view.id, view]),
      ).values(),
    ],
    [savedViews, sessionViews],
  );
  const groupedRows = useMemo(() => {
    if (!groupBy) return [{ label: "", rows: pageRows }];
    const groups = new Map<string, typeof pageRows>();
    for (const row of pageRows) {
      const label = tableText(valueAt(row.record, groupBy)) || "Not specified";
      groups.set(label, [...(groups.get(label) ?? []), row]);
    }
    return [...groups].map(([label, rows]) => ({ label, rows }));
  }, [pageRows, groupBy]);

  function togglePanel(next: typeof panel, button: HTMLButtonElement) {
    panelToggle.current = button;
    setPanel((current) => (current === next ? null : next));
  }
  async function changePins(next: Set<string>) {
    if (pinMutationPending.current) return;
    const previous = new Set(pins);
    pinMutationPending.current = true;
    setPinBusy(true);
    setPins(next);
    try {
      await onPinChange?.([...next]);
      // Server-controlled pins are never copied into browser storage.
      if (pinnedIds === undefined && storageKey && allowPersistNonPrivateIds) {
        try {
          localStorage.setItem(
            `zentrotax:pins:${storageKey}`,
            JSON.stringify([...next].slice(0, 10000)),
          );
        } catch {
          setMessage(
            "Pins updated for this session; browser storage is unavailable.",
          );
        }
      }
    } catch {
      setPins(
        latestPinnedIds.current === undefined
          ? previous
          : new Set(latestPinnedIds.current),
      );
      setMessage(
        "The pin change could not be saved. The latest known pins have been restored.",
      );
    } finally {
      // The callback must await its refresh. The next server prop remains authoritative,
      // including failures or a concurrent change; optimism never becomes hidden truth.
      if (latestPinnedIds.current !== undefined)
        setPins(new Set(latestPinnedIds.current));
      pinMutationPending.current = false;
      setPinBusy(false);
    }
  }
  function togglePin(id: string) {
    const next = new Set(pins);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    void changePins(next);
  }
  function toggleSelection(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function selectPage() {
    setSelected((current) => {
      const next = new Set(current);
      pageRows.forEach((row) =>
        allPageSelected ? next.delete(row.id) : next.add(row.id),
      );
      return next;
    });
  }
  function clearFilters() {
    setQuery("");
    setFilters([]);
    setPage(1);
  }
  function updateFilter(id: string, patch: Partial<TableFilter>) {
    setFilters((current) =>
      current.map((filter) =>
        filter.id === id ? { ...filter, ...patch } : filter,
      ),
    );
    setPage(1);
  }
  function exportRows(rows: T[], name: string) {
    if (!allowExport) return;
    if (!rows.length || !activeColumns.length) {
      setMessage("No visible records to export.");
      return;
    }
    const blob = new Blob(["\uFEFF", recordsToCsv(rows, activeColumns)], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob),
      link = document.createElement("a");
    link.href = url;
    link.download = `${name.replace(/[^a-zA-Z0-9_-]/g, "-").slice(0, 80) || "records"}.csv`;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    setMessage(
      `${rows.length.toLocaleString()} records exported. Spreadsheet formulas are neutralized.`,
    );
  }
  async function saveView() {
    if (busy || !viewValidation.validate()) return;
    const view: TableView = {
      id: `view:${Date.now()}:${++filterSerial.current}`,
      name: viewName.trim(),
      visibleColumns: [...visible],
      filters: filters.map((filter) => ({ ...filter })),
      sort,
      groupBy,
      pageSize,
      query,
    };
    setBusy(true);
    try {
      await onSaveView?.(view);
      setSessionViews((current) =>
        sessionViewsAfterSave(current, view, Boolean(onSaveView)),
      );
      setViewName("");
      setMessage(
        onSaveView
          ? `View “${view.name}” saved.`
          : `View “${view.name}” saved for this session.`,
      );
    } catch {
      setMessage(
        "The view could not be saved. Your current table settings are unchanged.",
      );
    } finally {
      setBusy(false);
    }
  }
  function applyView(view: TableView) {
    const keys = new Set(columns.map((column) => column.key));
    const usable = view.visibleColumns.filter((key) => keys.has(key));
    setVisible(usable.length ? usable : columns.map((column) => column.key));
    setFilters(view.filters.filter((filter) => keys.has(filter.field)));
    setSort(view.sort && keys.has(view.sort.key) ? view.sort : null);
    setGroupBy(keys.has(view.groupBy) ? view.groupBy : "");
    setPageSize([15, 30, 60, 100].includes(view.pageSize) ? view.pageSize : 15);
    setQuery(view.query ?? "");
    setPage(1);
    setPanel(null);
    setMessage(`View “${view.name}” applied.`);
  }
  async function performAction(action: {
    label: string;
    onAction: (rows: T[]) => void | Promise<void>;
  }) {
    if (!allowExport && /\bexport\b/i.test(action.label)) return;
    setBusy(true);
    try {
      await action.onAction(selectedRows.map((row) => row.record));
      setMessage(
        `${action.label}: ${selectedRows.length} selected records submitted.`,
      );
    } catch {
      setMessage(
        `${action.label} could not be completed. Selection is preserved.`,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      className="zt-data-table"
      aria-label={displayTitle}
      aria-busy={loading}
    >
      <div className="zt-table-toolbar">
        <div className="zt-table-search">
          <Search size={16} aria-hidden="true" />
          <label className="zt-sr-only" htmlFor={`${instance}-search`}>
            Search {displayTitle}
          </label>
          <input
            id={`${instance}-search`}
            placeholder="Search records…"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
          />
          {query && (
            <button
              type="button"
              className="zt-icon-button"
              aria-label="Clear search"
              onClick={() => {
                setQuery("");
                setPage(1);
              }}
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="zt-table-tools">
          <button
            type="button"
            className={`zt-tool-button ${panel === "filters" || filters.length ? "is-active" : ""}`}
            aria-expanded={panel === "filters"}
            aria-controls={`${instance}-filters`}
            onClick={(event) => togglePanel("filters", event.currentTarget)}
          >
            <Filter size={15} aria-hidden="true" />
            Filters
            {filters.length > 0 && (
              <span className="zt-count">{filters.length}</span>
            )}
          </button>
          <button
            type="button"
            className={`zt-tool-button ${panel === "columns" ? "is-active" : ""}`}
            aria-expanded={panel === "columns"}
            aria-controls={`${instance}-columns`}
            onClick={(event) => togglePanel("columns", event.currentTarget)}
          >
            <Columns3 size={15} aria-hidden="true" />
            <span>Columns</span>
          </button>
          <label className="zt-group-select">
            <Layers3 size={15} aria-hidden="true" />
            <span className="zt-sr-only">Group records by</span>
            <select
              value={groupBy}
              onChange={(event) => {
                setGroupBy(event.target.value);
                setPage(1);
              }}
            >
              <option value="">Group by</option>
              {columns.map((column) => (
                <option key={column.key} value={column.key}>
                  {taxText(column.label)}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className={`zt-tool-button ${panel === "views" ? "is-active" : ""}`}
            aria-expanded={panel === "views"}
            aria-controls={`${instance}-views`}
            onClick={(event) => togglePanel("views", event.currentTarget)}
          >
            <SlidersHorizontal size={15} aria-hidden="true" />
            Views
          </button>
          {allowExport && (
            <button
              type="button"
              className="zt-tool-button"
              disabled={!filtered.length || loading}
              onClick={() =>
                exportRows(
                  filtered.map((row) => row.record),
                  title,
                )
              }
              title="Export all filtered rows and visible columns"
            >
              <Download size={15} aria-hidden="true" />
              <span>Export</span>
            </button>
          )}
        </div>
      </div>
      {panel && (
        <div
          className="zt-table-panel"
          id={`${instance}-${panel}`}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setPanel(null);
              panelToggle.current?.focus();
            }
          }}
        >
          <div className="zt-panel-heading">
            <strong>
              {panel === "columns"
                ? "Visible columns"
                : panel === "filters"
                  ? "Match all conditions"
                  : "Your saved views"}
            </strong>
            <button
              type="button"
              className="zt-icon-button"
              aria-label="Close table options"
              onClick={() => {
                setPanel(null);
                panelToggle.current?.focus();
              }}
            >
              <X size={16} />
            </button>
          </div>
          {panel === "columns" && (
            <div className="zt-column-options">
              {columns.map((column) => (
                <label key={column.key}>
                  <input
                    className="zt-check"
                    type="checkbox"
                    checked={visible.includes(column.key)}
                    disabled={
                      visible.length === 1 && visible.includes(column.key)
                    }
                    onChange={() =>
                      setVisible((current) =>
                        current.includes(column.key)
                          ? current.filter((key) => key !== column.key)
                          : [...current, column.key],
                      )
                    }
                  />
                  {taxText(column.label)}
                </label>
              ))}
            </div>
          )}
          {panel === "filters" && (
            <div className="zt-filter-builder">
              {filters.length === 0 && (
                <p className="zt-muted">
                  Add a condition to focus on exactly the records you need.
                </p>
              )}
              {filters.map((filter, index) => (
                <div className="zt-filter-row" key={filter.id}>
                  <span className="zt-filter-and">
                    {index ? "and" : "where"}
                  </span>
                  <select
                    aria-label={`Condition ${index + 1} field`}
                    value={filter.field}
                    onChange={(event) =>
                      updateFilter(filter.id, { field: event.target.value })
                    }
                  >
                    {columns.map((column) => (
                      <option value={column.key} key={column.key}>
                        {taxText(column.label)}
                      </option>
                    ))}
                  </select>
                  <select
                    aria-label={`Condition ${index + 1} operator`}
                    value={filter.operator}
                    onChange={(event) =>
                      updateFilter(filter.id, {
                        operator: event.target.value as FilterOperator,
                      })
                    }
                  >
                    {operators.map((operator) => (
                      <option value={operator.value} key={operator.value}>
                        {operator.label}
                      </option>
                    ))}
                  </select>
                  {!["is_empty", "is_not_empty"].includes(filter.operator) && (
                    <input
                      aria-label={`Condition ${index + 1} value`}
                      placeholder="Enter a value"
                      value={filter.value}
                      onChange={(event) =>
                        updateFilter(filter.id, { value: event.target.value })
                      }
                    />
                  )}
                  <button
                    type="button"
                    className="zt-icon-button"
                    aria-label={`Remove condition ${index + 1}`}
                    onClick={() => {
                      setFilters((current) =>
                        current.filter((item) => item.id !== filter.id),
                      );
                      setPage(1);
                    }}
                  >
                    <X size={15} />
                  </button>
                </div>
              ))}
              <div className="zt-filter-actions">
                <button
                  type="button"
                  className="zt-tool-button"
                  disabled={!columns.length}
                  onClick={() =>
                    setFilters((current) => [
                      ...current,
                      {
                        id: `${instance}:${++filterSerial.current}`,
                        field: columns[0]?.key ?? "",
                        operator: "contains",
                        value: "",
                      },
                    ])
                  }
                >
                  <Plus size={14} />
                  Add condition
                </button>
                {filters.length > 0 && (
                  <button
                    type="button"
                    className="zt-text-button"
                    onClick={() => {
                      setFilters([]);
                      setPage(1);
                    }}
                  >
                    Clear conditions
                  </button>
                )}
              </div>
            </div>
          )}
          {panel === "views" && (
            <div className="zt-saved-views">
              {allViews.length ? (
                <div className="zt-view-chips">
                  {allViews.map((view) => (
                    <button
                      type="button"
                      className="zt-tool-button"
                      key={view.id}
                      onClick={() => applyView(view)}
                    >
                      <SlidersHorizontal size={14} />
                      {view.name}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="zt-muted">
                  Save your columns, filters, grouping and sort together.
                </p>
              )}
              <ValidationSummary issues={viewValidation.issues} />
              <form
                noValidate
                onSubmit={(event) => {
                  event.preventDefault();
                  void saveView();
                }}
              >
                <label className="zt-sr-only" htmlFor={`${instance}-view-name`}>
                  New view name
                </label>
                <input
                  {...viewValidation.fieldProps(`${instance}-view-name`)}
                  placeholder="Name this view"
                  value={viewName}
                  maxLength={80}
                  onChange={(event) => setViewName(event.target.value)}
                />
                <button
                  type="submit"
                  className="zt-solid-button"
                  disabled={busy}
                >
                  <Plus size={14} />
                  {busy ? "Saving…" : "Save view"}
                </button>
              </form>
              <FieldError
                issue={viewValidation.issue(`${instance}-view-name`)}
              />
              {!onSaveView && (
                <small className="zt-muted">
                  Views are stored in this page session only.
                </small>
              )}
            </div>
          )}
        </div>
      )}
      {selectedRows.length > 0 && (
        <div className="zt-bulk-bar">
          <span>
            <Check size={15} />
            {selectedRows.length.toLocaleString()} selected
          </span>
          {allowExport && (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                exportRows(
                  selectedRows.map((row) => row.record),
                  `${title}-selected`,
                )
              }
            >
              Export selected
            </button>
          )}
          <button
            type="button"
            disabled={busy || pinBusy}
            onClick={() => {
              void changePins(
                new Set([...pins, ...selectedRows.map((row) => row.id)]),
              );
            }}
          >
            Pin selected
          </button>
          {bulkActions
            .filter(
              (action) => allowExport || !/\bexport\b/i.test(action.label),
            )
            .map((action) => (
              <button
                type="button"
                key={action.label}
                disabled={busy}
                onClick={() => void performAction(action)}
              >
                {action.label}
              </button>
            ))}
          <button
            type="button"
            className="zt-bulk-clear"
            onClick={() => setSelected(new Set())}
            aria-label="Clear selection"
          >
            <X size={16} />
          </button>
        </div>
      )}
      <div
        className="zt-table-scroll"
        tabIndex={0}
        aria-label={`${displayTitle} table, scroll horizontally if needed`}
      >
        <table>
          <caption className="zt-sr-only">
            {displayTitle}. {filtered.length} matching records.
            {allowExport ? " Export includes all matching pages." : ""}
          </caption>
          <thead>
            <tr>
              <th className="zt-select-cell" scope="col">
                <SelectionBox
                  checked={allPageSelected}
                  mixed={somePageSelected && !allPageSelected}
                  label={`Select all ${pageRows.length} records on this page`}
                  onChange={selectPage}
                />
              </th>
              <th className="zt-pin-cell" scope="col">
                <span className="zt-sr-only">Pinned</span>
                <Pin size={13} aria-hidden="true" />
              </th>
              {activeColumns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  aria-sort={
                    sort?.key === column.key
                      ? sort.direction === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                >
                  {column.sortable === false ? (
                    taxText(column.label)
                  ) : (
                    <button
                      type="button"
                      className="zt-sort-button"
                      onClick={() => {
                        setSort((current) =>
                          current?.key === column.key
                            ? current.direction === "asc"
                              ? { key: column.key, direction: "desc" }
                              : null
                            : { key: column.key, direction: "asc" },
                        );
                        setPage(1);
                      }}
                    >
                      {taxText(column.label)}
                      {sort?.key === column.key ? (
                        sort.direction === "asc" ? (
                          <ArrowUp size={13} />
                        ) : (
                          <ArrowDown size={13} />
                        )
                      ) : (
                        <ArrowUpDown size={12} className="zt-sort-idle" />
                      )}
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 6 }, (_, index) => (
                  <tr key={`skeleton:${index}`} aria-hidden="true">
                    <td colSpan={activeColumns.length + 2}>
                      <div className="zt-table-skeleton" />
                    </td>
                  </tr>
                ))
              : groupedRows.map((group) => (
                  <GroupRows<T>
                    key={group.label || "ungrouped"}
                    group={group}
                    grouped={Boolean(groupBy)}
                    groupColumn={columns.find(
                      (column) => column.key === groupBy,
                    )}
                    columns={activeColumns}
                    pins={pins}
                    pinDisabled={pinBusy}
                    selected={selected}
                    onPin={togglePin}
                    onSelect={toggleSelection}
                    onInspect={onInspect}
                  />
                ))}
            {!loading && !pageRows.length && (
              <tr>
                <td colSpan={activeColumns.length + 2}>
                  <div className="zt-table-empty">
                    <div className="zt-empty-icon">
                      <Search size={24} />
                    </div>
                    <strong>
                      {query || filters.length
                        ? filteredEmptyTitle
                        : returnTable
                          ? "No returns yet"
                          : "A clear workspace"}
                    </strong>
                    <p>
                      {query || filters.length
                        ? "Try adjusting your search or removing a condition."
                        : taxText(emptyMessage)}
                    </p>
                    {(query || filters.length > 0) && (
                      <button
                        type="button"
                        className="zt-tool-button"
                        onClick={clearFilters}
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <footer className="zt-table-footer">
        <span>
          {filtered.length
            ? `${((effectivePage - 1) * pageSize + 1).toLocaleString()}–${Math.min(effectivePage * pageSize, filtered.length).toLocaleString()} of ${filtered.length.toLocaleString()}`
            : "0 records"}
          {filtered.length !== records.length && (
            <span className="zt-muted">
              {" "}
              · {records.length.toLocaleString()} total
            </span>
          )}
        </span>
        <div className="zt-pagination">
          <label>
            Rows
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
            >
              {[15, 30, 60, 100].map((size) => (
                <option value={size} key={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="zt-icon-button"
            aria-label="Previous page"
            disabled={effectivePage <= 1}
            onClick={() => setPage(effectivePage - 1)}
          >
            <ChevronLeft size={16} />
          </button>
          <span aria-live="polite">
            {effectivePage} / {pages}
          </span>
          <button
            type="button"
            className="zt-icon-button"
            aria-label="Next page"
            disabled={effectivePage >= pages}
            onClick={() => setPage(effectivePage + 1)}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </footer>
      <div
        className="zt-table-status zt-sr-only"
        role="status"
        aria-live="polite"
      >
        {message}
      </div>
    </section>
  );
}

function GroupRows<T extends TableRecord>({
  group,
  grouped,
  groupColumn,
  columns,
  pins,
  pinDisabled,
  selected,
  onPin,
  onSelect,
  onInspect,
}: {
  group: { label: string; rows: { record: T; id: string; index: number }[] };
  grouped: boolean;
  groupColumn?: TableColumn<T>;
  columns: TableColumn<T>[];
  pins: Set<string>;
  pinDisabled: boolean;
  selected: Set<string>;
  onPin: (id: string) => void;
  onSelect: (id: string) => void;
  onInspect?: (record: T) => void;
}) {
  return (
    <>
      {grouped && (
        <tr className="zt-group-heading">
          <th colSpan={columns.length + 2} scope="rowgroup">
            <Layers3 size={13} />
            {group.rows[0] && groupColumn
              ? columnDisplayText(group.rows[0].record, groupColumn) ||
                group.label
              : group.label}
            <span>{group.rows.length} on this page</span>
          </th>
        </tr>
      )}
      {group.rows.map(({ record, id, index }) => (
        <tr
          key={`${id}:${index}`}
          className={`${selected.has(id) ? "is-selected" : ""} ${pins.has(id) ? "is-pinned" : ""}`}
        >
          <td className="zt-select-cell">
            <SelectionBox
              checked={selected.has(id)}
              label={`Select ${columnDisplayText(record, columns[0]) || `record ${index + 1}`}`}
              onChange={() => onSelect(id)}
            />
          </td>
          <td className="zt-pin-cell">
            <button
              type="button"
              className={`zt-row-pin ${pins.has(id) ? "is-active" : ""}`}
              aria-label={`${pins.has(id) ? "Unpin" : "Pin"} record ${index + 1}`}
              aria-pressed={pins.has(id)}
              disabled={pinDisabled}
              onClick={() => onPin(id)}
            >
              <Pin size={13} />
            </button>
          </td>
          {columns.map((column, columnIndex) => {
            const value = valueAt(record, column.key);
            const content = column.render
              ? column.render(value, record)
              : tableText(value) || <span className="zt-cell-empty">—</span>;
            return (
              <td
                key={column.key}
                title={
                  column.getTitle
                    ? column.getTitle(value, record)
                    : typeof value === "string"
                      ? value
                      : undefined
                }
              >
                {columnIndex === 0 && onInspect ? (
                  <button
                    type="button"
                    className="zt-record-link"
                    onClick={() => onInspect(record)}
                  >
                    {content}
                  </button>
                ) : (
                  <div className="zt-cell-content">{content}</div>
                )}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

export default DataTable;
